import React from 'react';
import { Text } from '@fluentui/react';
import styles from './PriceComparison.module.css';

interface PriceComparisonProps {
    precio: number;
    precio_mercado_promedio?: number;
    precio_mercado_maximo?: number;
}

export const PriceComparison: React.FC<PriceComparisonProps> = ({ precio, precio_mercado_promedio, precio_mercado_maximo }) => {
    const getChartPosition = () => {
        if (!precio_mercado_promedio) return 50;
        
        const ratio = precio / precio_mercado_promedio;
        
        // Market average is at 50%
        // Map price position based on ratio
        // 0.7 ratio = 10% (far left)
        // 1.0 ratio = 50% (center, same as market)
        // 1.3 ratio = 90% (far right)
        
        if (ratio <= 1) {
            // Price is below or equal to market - position to the left
            // 0.7 -> 10%, 1.0 -> 50%
            return 10 + (ratio - 0.7) * (40 / 0.3);
        } else {
            // Price is above market - position to the right
            // 1.0 -> 50%, 1.3 -> 90%
            return 50 + (ratio - 1.0) * (40 / 0.3);
        }
    };
    
    const getMarketPosition = () => {
        // Market average is always at center
        return 50;
    };

    const getPriceDifference = () => {
        if (!precio_mercado_promedio) return null;
        return precio - precio_mercado_promedio;
    };
    
    const getPriceStatus = () => {
        if (!precio_mercado_promedio) {
            return { label: 'Sin datos', color: '#6b7280' };
        }
        
        // Using the exact same logic as PriceIndicator.tsx
        // precio <= promedio: "Buen Precio" with green
        if (precio <= precio_mercado_promedio) {
            return { label: 'Buen Precio', color: '#22c55e' };
        }
        
        // precio > promedio but <= maximo: "Precio Justo" with orange
        if (precio_mercado_maximo && precio > precio_mercado_promedio && precio <= precio_mercado_maximo) {
            return { label: 'Precio Justo', color: '#ffa500' };
        }
        
        // precio > maximo or no maximo data: "Revisa Precio" with orange
        return { label: 'Revisa Precio', color: '#ffa500' };
    };

    const chartPosition = Math.max(5, Math.min(95, getChartPosition())); // Keep within bounds
    const marketPosition = getMarketPosition();
    const priceDifference = getPriceDifference();
    const priceStatus = getPriceStatus();

    if (!precio_mercado_promedio) {
        return null;
    }

    return (
        <div className={styles.container}>
            <Text className={styles.title}>Comparación de precio</Text>
            
            <div className={styles.chartWrapper}>
                {/* Price label and status above */}
                <div className={styles.priceSection} style={{ left: `${chartPosition}%` }}>
                    <Text className={styles.priceLabel}>${Math.round(precio).toLocaleString('en-US', { maximumFractionDigits: 0 })}</Text>
                    <div className={styles.statusBadge} style={{ backgroundColor: priceStatus.color }}>
                        {priceStatus.label === 'Buen Precio' && <span className={styles.checkmark}>✓</span>}
                        {priceStatus.label === 'Precio Justo' && <span className={styles.checkmark}>✓</span>}
                        {priceStatus.label === 'Revisa Precio' && <span className={styles.checkmark}>!</span>}
                        <span className={styles.statusText}>{priceStatus.label}</span>
                    </div>
                </div>
                
                {/* Horizontal line and markers */}
                <div className={styles.chartContainer}>
                    <div className={styles.horizontalLine}>
                        {/* Current price marker */}
                        <div 
                            className={styles.priceMarker}
                            style={{ left: `${chartPosition}%` }}
                        />
                        
                        {/* Market average marker */}
                        <div 
                            className={styles.marketMarker}
                            style={{ left: `${marketPosition}%` }}
                        />
                    </div>
                </div>
                
                {/* Market average label below */}
                <div className={styles.marketSection} style={{ left: `${marketPosition}%` }}>
                    <Text className={styles.marketLabel}>${Math.round(precio_mercado_promedio).toLocaleString('en-US', { maximumFractionDigits: 0 })}</Text>
                    <Text className={styles.marketText}>Precio promedio del mercado (VMI)</Text>
                </div>
            </div>

            {priceDifference !== null && priceDifference !== 0 && (
                <Text className={styles.differenceText}>
                    {priceDifference < 0
                        ? `Este carro está $${Math.round(Math.abs(priceDifference)).toLocaleString('en-US', { maximumFractionDigits: 0 })} por debajo del precio de mercado. Lo comparamos con autos similares basado en precio, millas, accesorios, condición, y otros factores.`
                        : `Este carro está $${Math.round(Math.abs(priceDifference)).toLocaleString('en-US', { maximumFractionDigits: 0 })} por encima del precio de mercado. Lo comparamos con autos similares basado en precio, millas, accesorios, condición, y otros factores.`
                    }
                </Text>
            )}
        </div>
    );
};